import {
  CARRY_FORWARD_MODES,
  STANDARD_CARRY_FORWARD_GROUPS,
} from 'components/utils/carryForwardOnContinue';
import { hasOperationValue } from './capacityFallback';

// "Valider et continuer" pre-fills the next container with values from the container
// just recorded, by group (a TAV with its temperature, a volume temperature, each custom
// field). In every mode a group is carried only when entered on the container: one of its
// values is filled and differs from what the container had stored, or is the value
// carried into it, unchanged (so a series of containers keeps it). A group left untouched
// or cleared is never carried; a carried group takes all its values, empty ones included,
// so that a TAV and its temperature always come from the same container. The group's mode (see
// components/utils/carryForwardOnContinue) only decides where it lands:
// - replace: on the next container, replacing its values;
// - ifEmpty: only on a next container without the group's lead measure (its TAV, its
//   volume, or for a full container its volume temperature; a custom field is its own
//   lead), replacing its values there;
// - off: nowhere, the group is not carried.
// pickCarryForwardValues picks them when the container is recorded; the next
// container's getDefaultFormValues lands them with landCarriedValues. An empty
// container takes no carried measures; custom fields are still carried.
// The measurement method (liquid height, ullage or liquid volume) is carried unless the
// volume temperature's mode is off, and only lands on a container without a volume
// measure, whatever the mode: it only preselects the method of a container still to
// measure, and never hides a stored measure behind another method.

// Key of previousItemFormValues holding the container the values were carried from
export const CARRIED_FROM_KEY = 'carriedFrom';

// Form values are { value, unit } objects, except the measurement method (a string)
const rawValue = (fieldValue) => (
  fieldValue !== null && typeof fieldValue === 'object' ? fieldValue.value : fieldValue
);

const comparableValue = (fieldValue) => (
  hasOperationValue(rawValue(fieldValue)) ? String(rawValue(fieldValue)).trim() : ''
);

const hasFormFieldValue = (fieldValue) => comparableValue(fieldValue) !== '';

const VOLUME_LEAD_FORM_KEYS = STANDARD_CARRY_FORWARD_GROUPS.volumeTemperature.leadFormKeys;

const MEASUREMENT_METHOD_GROUP = {
  settingKey: 'volumeTemperature',
  formKeys: ['measurementType'],
  leadFormKeys: VOLUME_LEAD_FORM_KEYS,
  // A full container takes its volume from the capacity: the method is not used
  fullContainerLeadFormKeys: [],
  isMeasure: true,
  // In replace mode too: a container with a volume measure keeps its method
  landsOnlyWithoutLead: true,
};

const carryForwardGroups = (customFields = []) => [
  ...Object.entries(STANDARD_CARRY_FORWARD_GROUPS).map(([settingKey, group]) => ({
    settingKey, ...group, isMeasure: true,
  })),
  MEASUREMENT_METHOD_GROUP,
  ...customFields.map((fieldId) => ({ settingKey: fieldId, formKeys: [fieldId] })),
];

/**
 * Whether a form value counts as entered on the container: filled, and either different
 * from the stored value or the value carried into the container, kept unchanged. Landed
 * carried values are the very objects picked on the previous container, and an untouched
 * form field keeps its object; only { value, unit } objects keep an identity, an equal
 * method string proves nothing.
 */
const isEnteredValue = ({
  values, storedValues, carriedValues, formKey,
}) => hasFormFieldValue(values?.[formKey])
  && (comparableValue(values[formKey]) !== comparableValue(storedValues?.[formKey])
    || (typeof values[formKey] === 'object'
      && carriedValues?.[formKey] !== undefined
      && values[formKey] === carriedValues[formKey]));

/**
 * Values of the recorded container to pre-fill on the next one.
 * @param {Object} params
 * @param {Record<string, *>} params.values - submitted form values
 * @param {Record<string, *>} params.storedValues - form values built from the container's stored data
 * @param {Record<string, *>} [params.carriedValues] - values carried into the container
 *   (its previousItemFormValues): one still shown unchanged counts as entered, even when
 *   equal to the stored value (see isEnteredValue)
 * @param {(settingKey: string) => string} params.carryForwardModeOf - mode of a group (CARRY_FORWARD_MODES)
 * @param {string[]} [params.customFields] - custom operator field ids
 * @returns {Record<string, *>} form values to pass as the next container's previousItemFormValues
 */
export const pickCarryForwardValues = ({
  values,
  storedValues,
  carriedValues,
  carryForwardModeOf,
  customFields = [],
}) => carryForwardGroups(customFields).reduce((acc, { settingKey, formKeys }) => {
  const mode = carryForwardModeOf(settingKey);
  const entered = formKeys.some((formKey) => isEnteredValue({
    values, storedValues, carriedValues, formKey,
  }));
  if (mode !== CARRY_FORWARD_MODES.off && entered) {
    formKeys
      .filter((formKey) => values?.[formKey] !== undefined)
      .forEach((formKey) => { acc[formKey] = values[formKey]; });
  }
  return acc;
}, {});

/**
 * Pre-fills a container's form values with the values carried from the previous one.
 * @param {Object} params
 * @param {Record<string, *>} params.storedValues - form values built from the container's stored data
 * @param {Record<string, *>} [params.carriedValues] - from pickCarryForwardValues
 * @param {(settingKey: string) => string} params.carryForwardModeOf - mode of a group (CARRY_FORWARD_MODES)
 * @param {string[]} [params.customFields] - custom operator field ids
 * @param {boolean} [params.carryMeasures] - false for an empty container
 * @param {boolean} [params.isFull] - true for a full container (volume from the capacity)
 * @returns {Record<string, *>} the form values to show
 */
export const landCarriedValues = ({
  storedValues,
  carriedValues,
  carryForwardModeOf,
  customFields = [],
  carryMeasures = true,
  isFull = false,
}) => carryForwardGroups(customFields).reduce((acc, {
  settingKey, formKeys, leadFormKeys = formKeys, fullContainerLeadFormKeys = leadFormKeys, isMeasure,
  landsOnlyWithoutLead = false,
}) => {
  const carriedKeys = formKeys.filter((formKey) => carriedValues?.[formKey] !== undefined
    && Object.prototype.hasOwnProperty.call(storedValues, formKey));
  const hasStoredLead = (isFull ? fullContainerLeadFormKeys : leadFormKeys)
    .some((formKey) => hasFormFieldValue(storedValues[formKey]));
  const keepsStoredLead = landsOnlyWithoutLead
    || carryForwardModeOf(settingKey) === CARRY_FORWARD_MODES.ifEmpty;
  if (carriedKeys.length === 0
    || (isMeasure && !carryMeasures)
    || (keepsStoredLead && hasStoredLead)) {
    return acc;
  }
  const landed = { ...acc };
  carriedKeys.forEach((formKey) => { landed[formKey] = carriedValues[formKey]; });
  return landed;
}, storedValues);

export const CARRY_FORWARD_INDICATORS = {
  // Shows the value carried from the previous container
  carried: 'carried',
  // Will be carried to the next container, replacing its value
  willCarry: 'willCarry',
  // Will be carried to the next container only if it does not have the measure yet
  willCarryIfEmpty: 'willCarryIfEmpty',
};

/**
 * What the operator sees next to a field whose group is carried forward, matching what
 * "Valider et continuer" will do (see pickCarryForwardValues):
 * - 'carried' while the field shows, unchanged, the value carried from the previous
 *   container;
 * - 'willCarry' / 'willCarryIfEmpty' once a value of its group was entered on this
 *   container, by the same rule as pickCarryForwardValues (a carried value kept counts as
 *   entered), so that the group will go to the next container;
 * - null otherwise, and for an empty field.
 * @param {Object} params
 * @param {string} params.formKey
 * @param {Record<string, *>} params.values - current form values
 * @param {Record<string, *>} params.initialValues - form values the container opened with
 * @param {Record<string, *>} [params.storedValues] - form values built from the container's
 *   stored data, as pickCarryForwardValues compares them; the initial values when missing
 * @param {Record<string, *>} [params.carriedValues] - previousItemFormValues
 * @param {(settingKey: string) => string} params.carryForwardModeOf - mode of a group (CARRY_FORWARD_MODES)
 * @param {string[]} [params.customFields] - custom operator field ids
 * @returns {string|null} one of CARRY_FORWARD_INDICATORS, or null
 */
export const carryForwardIndicator = ({
  formKey,
  values,
  initialValues,
  storedValues = initialValues,
  carriedValues,
  carryForwardModeOf,
  customFields = [],
}) => {
  const group = carryForwardGroups(customFields).find(({ formKeys }) => formKeys.includes(formKey));
  const mode = group ? carryForwardModeOf(group.settingKey) : CARRY_FORWARD_MODES.off;
  if (mode === CARRY_FORWARD_MODES.off || !hasFormFieldValue(values?.[formKey])) {
    return null;
  }
  // Landed carried values are the very objects picked on the previous container. The
  // method is a plain string, so an equal stored method could pass for a carried one:
  // it only lands on a container without a volume measure (whose measures are never
  // carried, so the initial values show the stored ones).
  const groupLanded = !group.landsOnlyWithoutLead
    || !group.leadFormKeys.some((key) => hasFormFieldValue(initialValues?.[key]));
  const isCarried = (key) => groupLanded
    && carriedValues?.[key] !== undefined
    && initialValues?.[key] === carriedValues[key];
  const isChanged = (key) => comparableValue(values?.[key]) !== comparableValue(initialValues?.[key]);

  if (isCarried(formKey) && !isChanged(formKey)) {
    return CARRY_FORWARD_INDICATORS.carried;
  }
  const entered = group.formKeys.some((key) => isEnteredValue({
    values, storedValues, carriedValues, formKey: key,
  }));
  if (!entered) {
    return null;
  }
  return mode === CARRY_FORWARD_MODES.ifEmpty
    ? CARRY_FORWARD_INDICATORS.willCarryIfEmpty
    : CARRY_FORWARD_INDICATORS.willCarry;
};
